import Vue from 'vue';
import Router from 'vue-router';
import User from '../user.js';
import store from '../store.js';
import { initializeAnalytics } from '../tracking';

// Containers
const TheContainer = () => import('@/containers/TheContainer');

// Views
const Dashboard = () => import('@/views/Dashboard');
const Login = () => import('@/views/Login');
const Register = () => import('@/views/Register');
const ForgotPassword = () => import('@/views/ForgotPassword');
const ResetPassword = () => import('@/views/ResetPassword');
const ConfirmEmail = () => import('@/views/ConfirmEmail');
const ActivateAccount = () => import('@/views/ActivateAccount');
const ForgotEmail = () => import('@/views/ForgotEmail');
const TemplatesManager = () => import('@/views/TemplatesManager');
const UpdateFormExtractionTemplate = () =>
  import('@/views/UpdateFormExtractionTemplate');
const UpdateDocumentMatchingTemplate = () =>
  import('@/views/UpdateDocumentMatchingTemplate');
const AddFormExtractionTemplate = () =>
  import('@/views/AddFormExtractionTemplate');
const AddDocumentMatchingTemplate = () =>
  import('@/views/AddDocumentMatchingTemplate');
const LicenseManagement = () => import('@/views/LicenseManagement.vue');
const UserManagement = () => import('@/views/UserManagement.vue');
const QuestionBank = () => import('@/views/QuestionBank.vue');
const Metrics = () => import('@/views/Metrics.vue');
const UserMetrics = () => import('@/views/UserMetrics.vue');
const NotFound = () => import('@/views/NotFound.vue');

Vue.use(Router);

const router = new Router({
  mode: 'history', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'active',
  scrollBehavior: () => ({ y: 0 }),
  routes: configRoutes()
});

const DEFAULT_TITLE = 'DataSnipper Dashboard';

router.beforeEach((to, _from, next) => {
  document.title = to.meta?.title
    ? `${to.meta.title} | ${DEFAULT_TITLE}`
    : DEFAULT_TITLE;
  next();
});

router.afterEach((to) => {
  const hasGATrackingCode = !!process.env.VUE_APP_GA_TRACKING_ID;
  const isGAInitialized = window.gtag !== undefined;
  if (hasGATrackingCode && isGAInitialized) {
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href.toLowerCase(),
      page_path: to.fullPath.toLowerCase()
    });
  }
});

function configRoutes() {
  return [
    {
      path: '/Login',
      name: 'Login',
      meta: {
        title: 'Login'
      },
      component: Login,
      beforeEnter: async function (to, from, next) {
        if (to.query.aud && to.query.token) {
          if (await User.adminLogin(to.query.aud, to.query.token)) {
            next('/metrics');
            return;
          }
        }
        next();
      }
    },
    {
      path: '/Register',
      name: 'Register',
      meta: { title: 'Register' },
      component: Register
    },
    {
      path: '/ForgotPassword',
      name: 'ForgotPassword',
      meta: { title: 'Forgot Password' },
      component: ForgotPassword
    },
    {
      path: '/ResetPassword',
      name: 'ResetPassword',
      meta: { title: 'Reset Password' },
      component: ResetPassword
    },
    {
      path: '/ConfirmEmail',
      name: 'ConfirmEmail',
      meta: { title: 'Confirm Email' },
      component: ConfirmEmail
    },
    {
      path: '/ActivateAccount',
      name: 'ActivateAccount',
      meta: { title: 'Activate Account' },
      component: ActivateAccount
    },
    {
      path: '/ForgotEmail',
      name: 'ForgotEmail',
      meta: { title: 'Forgot Email' },
      component: ForgotEmail
    },
    {
      path: '/',
      name: 'Home',
      redirect: '/Dashboard',
      component: TheContainer,
      beforeEnter: async function (to, from, next) {
        initializeAnalytics();
        await User.loadStatus();
        await User.loadChargeBeeSubscription();
        await User.loadIntercomSettings();
        next();
      },
      children: [
        {
          path: '/Dashboard',
          name: 'Dashboard',
          component: Dashboard,
          meta: { title: 'Metrics' },
          beforeEnter: function (to, _from, next) {
            // 'Dashboard' page is deprecated redirect users to new Metrics page
            // Allow access with ?force query param for backwards compatibility
            if (to.query.force !== undefined) {
              return next();
            }
            return next('/metrics');
          }
        },
        {
          path: '/LicenseManagement',
          name: 'LicenseManagement',
          meta: { title: 'License Management' },
          component: LicenseManagement,
          beforeEnter: (to, from, next) => {
            if (store.getters.isSelfServe) {
              next();
            } else {
              next('/404');
            }
          }
        },
        {
          path: '/UserManagement',
          name: 'UserManagement',
          meta: { title: 'User Management' },
          component: UserManagement,
          beforeEnter: (to, from, next) => {
            if (store.getters.isUmsEnabled) {
              next();
            } else {
              next('/404');
            }
          }
        },
        {
          path: '/QuestionBank',
          name: 'QuestionBank',
          meta: { title: 'Question Bank' },
          component: QuestionBank,
          beforeEnter: (to, from, next) => {
            if (store.getters.hasDocuMine) {
              next();
            } else {
              next('/404');
            }
          }
        },
        {
          path: '/metrics',
          name: 'Metrics',
          meta: { title: 'Metrics' },
          component: Metrics
        },
        {
          path: '/metrics/users',
          name: 'User Metrics',
          meta: { title: 'User Metrics' },
          component: UserMetrics
        },
        {
          path: '/404',
          name: 'NotFound',
          meta: { title: 'Not found' },
          component: NotFound
        },
        {
          path: '/TemplatesManager',
          name: 'TemplatesManager',
          meta: { title: 'Templates' },
          reload: true,
          component: TemplatesManager
        },
        {
          path: '/TemplatesManager/FormExtraction/UpdateTemplate',
          name: 'UpdateFormExtractionTemplate',
          meta: { title: 'Update Form Extraction Template' },
          component: UpdateFormExtractionTemplate
        },
        {
          path: '/TemplatesManager/DocumentMatching/UpdateTemplate',
          name: 'UpdateDocumentMatchingTemplate',
          meta: { title: 'Update Document Matching Template' },
          component: UpdateDocumentMatchingTemplate
        },
        {
          path: '/TemplatesManager/FormExtraction/AddTemplate',
          name: 'AddFormExtractionTemplate',
          meta: { title: 'Add Form Extraction Template' },
          component: AddFormExtractionTemplate
        },
        {
          path: '/TemplatesManager/DocumentMatching/AddTemplate',
          name: 'AddDocumentMatchingTemplate',
          meta: { title: 'Add Document Matching Template' },
          component: AddDocumentMatchingTemplate
        }
      ]
    },
    {
      path: '*',
      redirect: '/404'
    }
  ];
}

export default router;
